import type { ReactNode } from "react";

import { cn } from "../../lib/cn";

export interface CostCategory {
  id: string;
  name: string;
  amount: number;
  colorClass?: string;
  percentage?: number;
}

export interface CostBreakdownProps {
  currentTotal: number;
  projectedTotal?: number;
  budgetLimit?: number;
  currency?: string;
  period?: string;
  categories: CostCategory[];
  action?: ReactNode;
  className?: string;
}

/**
 * Composant de décomposition et suivi des coûts Cloud / FinOps.
 *
 * ── Transparence financière et contrôle des coûts ────────────────────────
 * Permet aux équipes DevOps et FinOps d'anticiper le burn-rate mensuel,
 * de visualiser la répartition par type de service (Calcul, Data, Réseau)
 * et de détecter immédiatement un dépassement de budget.
 */
export function CostBreakdown({
  currentTotal,
  projectedTotal,
  budgetLimit,
  currency = "€",
  period = "Mois en cours",
  categories,
  action,
  className,
}: CostBreakdownProps) {
  const formatCurrency = (val: number) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency === "€" ? "EUR" : currency === "$" ? "USD" : currency,
      maximumFractionDigits: 2,
    }).format(val);

  const calculatedTotal = categories.reduce((sum, c) => sum + c.amount, 0) || currentTotal;
  const isOverBudget = budgetLimit ? (projectedTotal ?? currentTotal) > budgetLimit : false;

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-xl border border-border bg-surface-raised p-5 shadow-xs",
        className,
      )}
    >
      {/* En-tête avec coût actuel & projection */}
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border pb-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">{period}</span>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold tracking-tight text-fg tabular-nums">
              {formatCurrency(currentTotal)}
            </span>
            {projectedTotal !== undefined && (
              <span className="text-xs text-fg-muted">
                Est. fin de mois : <strong className="font-semibold text-fg tabular-nums">{formatCurrency(projectedTotal)}</strong>
              </span>
            )}
          </div>
        </div>

        {budgetLimit !== undefined && (
          <div className="flex flex-col items-end">
            <span className="text-xs text-fg-subtle">Budget mensuel</span>
            <span
              className={cn(
                "text-sm font-semibold tabular-nums",
                isOverBudget ? "text-danger-fg font-bold" : "text-fg",
              )}
            >
              {formatCurrency(budgetLimit)}
            </span>
          </div>
        )}
      </div>

      {/* Barre visuelle de répartition empilée */}
      <div className="flex flex-col gap-2">
        <div className="flex h-3 w-full overflow-hidden rounded-full bg-surface-sunken inset-shadow-control">
          {categories.map((cat) => {
            const pct = (cat.amount / calculatedTotal) * 100;
            return (
              <div
                key={cat.id}
                style={{ width: `${pct}%` }}
                className={cn("h-full transition-all duration-slow", cat.colorClass ?? "bg-accent")}
                title={`${cat.name}: ${formatCurrency(cat.amount)} (${Math.round(pct)}%)`}
              />
            );
          })}
        </div>

        {/* Légende détaillée par catégorie */}
        <div className="grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => {
            const pct = Math.round((cat.amount / calculatedTotal) * 100);
            return (
              <div key={cat.id} className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className={cn("size-2.5 rounded-full shrink-0", cat.colorClass ?? "bg-accent")}
                  />
                  <span className="truncate text-fg-muted">{cat.name}</span>
                </div>
                <span className="font-medium text-fg tabular-nums shrink-0">
                  {formatCurrency(cat.amount)} ({pct}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {action && <div className="pt-2 border-t border-border/50 flex justify-end">{action}</div>}
    </div>
  );
}
